/***********************
 * My profile
 ************************/

import React, { useState, useEffect, useContext, useRef } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"

import GlobalState from "contexts/GlobalState"
import Breadcrumb from "../../components/Common/Breadcrumb"
import QuickGuide from "components/QuickGuides/QuickGuide"

import { getUserInfo } from "helpers/backend_helper"
import { kcUpdatePassword, kcUpdateProfile } from "helpers/keycloak_api_helper"
import { showToast } from "utils/general"

import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  Form,
  CardTitle,
  FormGroup,
  FormFeedback,
} from "reactstrap"
import { Tooltip, Popconfirm } from "antd"

import * as Yup from "yup"
import { useFormik } from "formik"
import { useNavigate } from "react-router-dom"

const UserProfile = props => {
  const [state, setState] = useContext(GlobalState)
  const [quickGuide, setQuickGuide] = useState(false)
  const [role, setRole] = useState("")
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [gsm, setGsm] = useState("")
  const [unit, setUnit] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [videoNickname, setVideoNickname] = useState("")
  const [editMode, setEditMode] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [showPasswordChange, setShowPasswordChange] = useState(false)

  const abortRef = useRef(null)
  const navigate = useNavigate()
  useEffect(() => {
    document.title = props.t("My profile") + " | Suvanto Care"
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (state.authUser != null) {
      const obj = JSON.parse(state.authUser)
      setName(obj.username)
      setEmail(obj.email)
      setGsm(obj.gsm)
      setFirstName(obj.firstName)
      setLastName(obj.lastName)
      setVideoNickname(obj.videoNickname)
      setUnit(obj.unit)

      switch (obj.role) {
        case "system_admin":
          setRole("System Admin")
          break
        case "organisation_admin":
          setRole("Organisation Admin")
          break
        case "organisation_user":
          setRole("Organisation User")
          break
        case "private_user":
          setRole("Private User")
          break
        case "salesperson":
          setRole("Salesperson")
          break
        case "order_manager":
          setRole("Order Manager")
          break
        default:
          break
      }
    }

    // Enable password change for anyone except Entra only users
    if (
      state.realm_roles == null ||
      !state.realm_roles.includes("entra_login_only")
    ) {
      setShowPasswordChange(true)
    }
  }, [state])

  // Fetch user info
  const getInfo = () => {
    getUserInfo(abortRef.current)
      .then(data => {
        if (data.username != null) {
          setState(state => ({ ...state, authUser: JSON.stringify(data) }))
        }
      })
      .catch(err => {
        console.log(err)
      })
  }

  // Update password request
  const updatePasswordRequest = () => {
    if (!updating) {
      setUpdating(true)
      kcUpdatePassword(abortRef.current)
        .then(result => {
          if (result == "OK") {
            navigate("/logout")
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setUpdating(false)
        })
    }
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: firstName,
      lastName: lastName,
      gsm: gsm,
      videoNickname: videoNickname || "",
    },
    validationSchema: Yup.object({
      videoNickname: Yup.string()
        .matches(
          /^[a-zA-Z0-9\s\-()!?&.,+#äöåøæÄÖÅÆØ]*$/,
          props.t("ContainsNotAllowedCharacters")
        )
        .trim(),
      firstName: Yup.string()
        .matches(
          /^[a-zA-Z0-9\s\-()!?&.,+#äöåøæÄÖÅÆØ]*$/,

          props.t("ContainsNotAllowedCharacters")
        )
        .trim()
        .required(props.t("Required field")),
      lastName: Yup.string()
        .matches(
          /^[a-zA-Z0-9\s\-()!?&.,+#äöåøæÄÖÅÆØ]*$/,
          props.t("ContainsNotAllowedCharacters")
        )
        .trim()
        .required(props.t("Required field")),
    }),
    onSubmit: (values, { resetForm }) => {
      // Trim values
      const keys = Object.keys(values)
      for (const key of keys) {
        values[key] = values[key].trim()
      }

      if (!values.videoNickname) {
        values.videoNickname = `${values.firstName} ${values.lastName}`
      }
      setUpdating(true)
      kcUpdateProfile(
        abortRef.current,
        values.firstName,
        values.lastName,
        values.gsm,
        values.videoNickname
      )
        .then(result => {
          console.log(result)
          showToast(props.t("Information updated"), "success")
          setEditMode(false)
          getInfo()
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Failed to update information"), "error")
        })
        .finally(() => {
          setUpdating(false)
        })
    },
  })

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid className={quickGuide ? "quick-guide-open" : ""}>
          <Breadcrumb
            title={props.t("My profile")}
            className="breadcrumb m-0 justify-content-center"
            breadcrumbItem={
              <div className="d-flex align-items-center">
                <QuickGuide
                  source="userProfile"
                  title="My profile"
                  isOpen={quickGuide}
                  setIsOpen={setQuickGuide}
                />
              </div>
            }
          />
          <Row>
            <Col lg="6">
              <Card>
                <CardBody className="mb-3">
                  <CardTitle className="mb-4">
                    <Row>
                      <Col className="d-flex" style={{ alignItems: "center" }}>
                        <i className="bx bxs-user-circle text-truncate align-middle me-2"></i>
                        {props.t("User information")}
                      </Col>
                      <Col className="col-auto">
                        {/**!editMode && (
                          <Button
                            color="primary"
                            size="sm"
                            className=""
                            onClick={() => {
                              setEditMode(true)
                            }}
                          >
                            <i className="me-2 bx bxs-edit text-truncate align-middle"></i>
                            <span className="">{props.t("Edit")}</span>
                          </Button>
                        )**/}
                        {editMode && (
                          <>
                            <Button
                              color="primary"
                              size="sm"
                              className=""
                              onClick={() => {
                                setEditMode(false)
                                validation.resetForm()
                              }}
                            >
                              <i className="me-2 bx bxs-x-square text-truncate align-middle"></i>
                              <span className="">{props.t("Cancel")}</span>
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              disabled={updating}
                              className="ms-3"
                              onClick={() => {
                                validation.submitForm()
                              }}
                            >
                              <i className="me-2 bx bxs-save text-truncate align-middle"></i>
                              <span className="">
                                {props.t("Save changes")}
                              </span>
                            </Button>
                          </>
                        )}
                      </Col>
                    </Row>
                  </CardTitle>

                  {!editMode && (
                    <>
                      <Row xs={1} sm={2} className="mt-3">
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Name")}:</h6>{" "}
                            {firstName + " " + lastName}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Username")}:</h6> {name}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("GSM")}:</h6> {gsm}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Video call display name")}:</h6>
                            {videoNickname}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Email")}:</h6> {email}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Role")}:</h6>{" "}
                            {props.t("UserRoles." + role)}
                          </div>
                        </Col>
                        <Col>
                          <div className="text-muted mb-4">
                            <h6>{props.t("Unit info")}:</h6>{" "}
                            {unit || props.t("Undefined")}
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}

                  {editMode && (
                    <>
                      <Form
                        className="form-horizontal"
                        onSubmit={e => {
                          e.preventDefault()
                          return false
                        }}
                      >
                        <Row xs={1} sm={2} className="mt-3">
                          <Col>
                            <FormGroup>
                              <Label>{props.t("First name")}</Label>
                              <Input
                                name="firstName"
                                type="text"
                                value={validation.values.firstName}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                invalid={
                                  !!(
                                    validation.touched.firstName &&
                                    validation.errors.firstName
                                  )
                                }
                              ></Input>
                              {validation.touched.firstName &&
                              validation.errors.firstName ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.firstName}
                                </FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("Last name")}</Label>
                              <Input
                                name="lastName"
                                type="text"
                                value={validation.values.lastName}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                invalid={
                                  !!(
                                    validation.touched.lastName &&
                                    validation.errors.lastName
                                  )
                                }
                              ></Input>
                              {validation.touched.lastName &&
                              validation.errors.lastName ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.lastName}
                                </FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>{props.t("GSM")}</Label>
                              <Input
                                name="gsm"
                                type="text"
                                value={validation.values.gsm}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                              ></Input>
                            </FormGroup>
                          </Col>
                          <Col>
                            <FormGroup>
                              <Label>
                                {props.t("Video call display name")}
                              </Label>
                              <div
                                style={{
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Input
                                  name="videoNickname"
                                  type="text"
                                  value={validation.values.videoNickname}
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  invalid={
                                    !!(
                                      validation.touched.videoNickname &&
                                      validation.errors.videoNickname
                                    )
                                  }
                                  style={{ paddingRight: "30px" }}
                                />
                                <Tooltip
                                  placement={"right"}
                                  trigger={["hover", "focus"]}
                                  title={props.t(
                                    "If a display name is not set, the first and last name will be used."
                                  )}
                                >
                                  <button
                                    aria-label={props.t(
                                      "If a display name is not set, the first and last name will be used."
                                    )}
                                    style={{
                                      position: "absolute",
                                      right: "10px",
                                      marginTop: "5px",
                                      fontSize: "1rem",
                                      cursor: "pointer",
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                    }}
                                  >
                                    <i className="dripicons-question text-muted" />
                                  </button>
                                </Tooltip>
                              </div>
                              {validation.touched.videoNickname &&
                              validation.errors.videoNickname ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.videoNickname}
                                </FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                        </Row>
                      </Form>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
            {/* Change password */}
            {showPasswordChange && (
              <Col lg="6">
                <Card>
                  <CardBody>
                    <CardTitle
                      className="mb-4 "
                      style={{ alignItems: "center" }}
                    >
                      <Row>
                        <Col>
                          <i className="bx bxs-lock-open text-truncate align-middle me-2"></i>
                          {props.t("Change password")}
                        </Col>
                      </Row>
                    </CardTitle>
                    <Alert color="info">{props.t("ChangePasswordInfo")}</Alert>
                    <Row className="py-1">
                      <Col></Col>
                      <Col className="col-auto">
                        <Popconfirm
                          title={props.t("Change password") + "?"}
                          okText={props.t("Yes")}
                          cancelButtonProps={{
                            className: "btn-color-primary-outline-hover",
                          }}
                          placement="bottom"
                          onConfirm={() => {
                            updatePasswordRequest()
                          }}
                        >
                          <Button color="primary" disabled={updating}>
                            {props.t("Change password")}
                          </Button>
                        </Popconfirm>
                      </Col>
                      <Col></Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

UserProfile.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(UserProfile)
